import { AxiosError } from 'axios'
import * as TypedError from 'error/typed'
import { Request } from 'express'
import get from 'lodash-es/get'
import isError from 'lodash-es/isError'
import isString from 'lodash-es/isString'
import rollbar from './rollbar'

const AXIOS_RESPONSE_DATA_MAX_LENGTH = 1000

export const UndefinedError = TypedError({
  type: 'undefined',
  message: 'Exception or rejection value was null or undefined'
})

export const NotAnError = TypedError({
  type: 'not-an-error',
  message: 'Exception or rejection value was not an error object',
  value: null
})

export const ResponseDataError = TypedError({
  type: 'response-data-error',
  message: 'response data error'
})

export const LandingPageNoContentError = TypedError({
  type: 'landing-page.no-content',
  level: 'warning',
  response: { status: 404 },
  message: 'Content does not exist for landing page'
})

export const LandingPageUnpublishedError = TypedError({
  type: 'landing-page.unpublished',
  level: 'warning',
  response: { status: 404 },
  message: 'Landing page is unpublished'
})

export const MetadataGenerationError = TypedError({
  type: 'metadata',
  title: 'Metadata',
  message: '{title}: {origMessage}'
})

export const MetadataGenerationWarning = TypedError({
  type: 'metadata',
  title: 'Metadata',
  message: '{title}: {origMessage}',
  level: 'warning'
})

export const WebSocketError = TypedError({
  type: 'websocket',
  message: 'websocket error'
})

export const isNetworkError = (error: any) => {
  return get(error, 'message') === 'Network Error'
}

export const isTimeoutError = (error: any) => {
  const status = get(error, 'response.status')
  const code = get(error, 'code')

  return [408, 504].includes(status) || code === 'ECONNABORTED'
}

export const isCriticalError = (error: any) => {
  const status = get(error, 'response.status')
  const level = get(error, 'level')
  return [422, 500, 502, 503].includes(status)
}

export const isWarning = (error: any) => {
  const status = get(error, 'response.status')
  const level = get(error, 'level')
  return (
    [400, 401].includes(status) ||
    isTimeoutError(error) ||
    isNetworkError(error)
  )
}

export const isAxiosError = (error: any): error is AxiosError => {
  const request = get(error, 'request')
  const config = get(error, 'config')
  return !!(config && request)
}

export const getAxiosErrorSummary = (
  error: AxiosError,
  maxResponseLength: number
) => {
  const url = error.config.url
  const endpoint = url && url.split('?')[0]
  let message = error.message
  let status
  let responseData

  if (error.response) {
    status = error.response.status
    responseData = error.response.data
    message = `Request [${endpoint}] failed with status code ${status}`

    if (responseData) {
      let nextResponseData = responseData
      if (!isString(nextResponseData)) {
        nextResponseData = JSON.stringify(nextResponseData)
      }
      if (nextResponseData.length > maxResponseLength) {
        responseData = nextResponseData.substr(0, maxResponseLength)
      }
    }
  }

  return {
    url,
    status,
    responseData,
    endpoint,
    message
  }
}

export enum LogErrorLevel {
  critical = 'critical',
  error = 'error',
  warning = 'warning'
}

interface ILogErrorOptions {
  data?: { [key: string]: any }
  request?: Request
  level?: LogErrorLevel
}

/**
 * Log error to rollbar
 * @param {Error} error
 */
export const logError = (error: any, options: ILogErrorOptions = {}): Error => {
  if (isError(error)) {
    const { data = {}, request, level } = options
    let message = error.message

    // Enhance logging for AxiosErrors
    // Originally Rollbar for AxiosErrors logs only message "Request failed with code 404" without
    // any info about endpoint/url or parameters which is not helpful
    if (isAxiosError(error)) {
      data.axios = getAxiosErrorSummary(error, AXIOS_RESPONSE_DATA_MAX_LENGTH)
      message = data.axios.message
    }

    if (level) {
      rollbar[level](message, error, data, request)
    } else {
      if (isWarning(error)) {
        rollbar.warning(message, error, data, request)
      } else {
        rollbar.error(message, error, data, request)
      }
    }

    return error
  } else if (error) {
    return logError(NotAnError({ value: error }))
  } else {
    return logError(UndefinedError())
  }
}
